import { Controller } from '@hotwired/stimulus'
import consumer from '../channels/consumer'
import { embedSignature, onSignliftEvent, finalizeSignature, SIGNLIFT_EVENTS } from '../lib/signlift'

const READY_STEP = 2
const SIGNATURE_STEP = 3

export default class extends Controller {
  static targets = [
    'waiting',
    'ready',
    'signature',
    'awaitingFinalization',
    'confirmation',
    'error',
    'budgetNotice',
    'progress',
    'iframeContainer',
  ]

  static values = {
    channelParams: Object,
    finalizeUrl: String,
    stepCount: { type: Number, default: 4 },
    autoConnect: { type: Boolean, default: true },
    reloadAfterSigned: { type: Boolean, default: false },
    errorDelay: { type: Number, default: 5000 },
  }

  connect() {
    this.cleanupSignlift = null
    this.finalizeInflight = false

    if (this.autoConnectValue) this.start()
  }

  disconnect() {
    clearTimeout(this.errorTimer)
    this.cleanupSignlift?.()
    this.stopListening()
  }

  start() {
    if (this.channel) return

    this.channel = consumer.subscriptions.create(this.channelParamsValue, {
      received: this.#received.bind(this),
      rejected: this.#showErrorLater.bind(this),
    })
  }

  stopListening() {
    if (!this.channel) return

    consumer.subscriptions.remove(this.channel)
    this.channel = null
  }

  showSignatureStep() {
    if (this.hasWaitingTarget) this.waitingTarget.classList.add('d-none')
    if (this.hasReadyTarget) this.readyTarget.classList.add('d-none')

    this.signatureTarget.classList.remove('d-none')
    this.#setProgress(SIGNATURE_STEP)
  }

  advanceToConfirmation() {
    if (this.hasWaitingTarget) this.waitingTarget.classList.add('d-none')
    if (this.hasReadyTarget) this.readyTarget.classList.add('d-none')
    if (this.hasBudgetNoticeTarget) this.budgetNoticeTarget.classList.add('d-none')

    this.signatureTarget.classList.add('d-none')

    if (this.hasConfirmationTarget) this.confirmationTarget.classList.remove('d-none')

    this.#setProgress(this.stepCountValue)
  }

  #received(data) {
    try {
      if (data.status === 'signed') {
        this.advanceToConfirmation()
      } else if (data.status === 'awaiting_finalization') {
        this.#displayAwaitingFinalization()
      } else if (data.status === 'success') {
        this.finalizeUrlValue = data.finalize_url || ''
        this.#embedDocument(data)
        this.#displayReadyStep()
        this.dispatch('contract-ready')
      } else {
        this.#rememberError(data.error)
        this.#showErrorLater()
      }
    } catch (error) {
      this.#rememberError(error.message)
      this.#showErrorLater()
    }
  }

  #rememberError(message) {
    this.lastError = message || ''
    if (this.lastError) console.error('[signature]', this.lastError)
  }

  #displayAwaitingFinalization() {
    clearTimeout(this.errorTimer)

    if (!this.hasAwaitingFinalizationTarget) {
      this.advanceToConfirmation()
      return
    }

    if (this.hasWaitingTarget) this.waitingTarget.classList.add('d-none')
    if (this.hasReadyTarget) this.readyTarget.classList.add('d-none')
    if (this.hasErrorTarget) this.errorTarget.classList.add('d-none')

    this.signatureTarget.classList.add('d-none')
    this.awaitingFinalizationTarget.classList.remove('d-none')

    this.#setProgress(SIGNATURE_STEP)
  }

  #embedDocument(data) {
    if (data.signing_url) {
      this.#embedSigningSession(data.signing_url)
    } else if (data.document_url) {
      this.#embedContractPreview(data.document_url)
    }
  }

  #embedSigningSession(signingUrl) {
    if (!this.hasIframeContainerTarget) return

    const iframe = embedSignature(this.iframeContainerTarget, signingUrl)

    this.cleanupSignlift?.()
    this.cleanupSignlift = onSignliftEvent(({ type }) => {
      if (type === SIGNLIFT_EVENTS.SUCCESS) {
        this.advanceToConfirmation()
        this.#triggerFinalize()
      } else if (type === SIGNLIFT_EVENTS.ERROR) {
        this.#showError()
      }
    }, signingUrl, iframe)
  }

  #embedContractPreview(documentUrl) {
    const iframe = document.createElement('iframe')
    iframe.src = documentUrl
    iframe.className = 'signature-iframe'

    this.signatureTarget.prepend(iframe)
  }

  #displayReadyStep() {
    if (this.hasReadyTarget) {
      if (this.hasWaitingTarget) this.waitingTarget.classList.add('d-none')

      this.readyTarget.classList.remove('d-none')
    }

    this.#setProgress(READY_STEP)
  }

  #triggerFinalize() {
    if (!this.finalizeUrlValue || this.finalizeInflight) return
    this.finalizeInflight = true

    const request = finalizeSignature(this.finalizeUrlValue)

    if (this.reloadAfterSignedValue) request.finally(() => window.location.reload())
  }

  #showErrorLater() {
    this.errorTimer = setTimeout(() => this.#showError(), this.errorDelayValue)
  }

  #showError() {
    if (this.hasWaitingTarget) this.waitingTarget.classList.add('d-none')
    if (this.hasReadyTarget) this.readyTarget.classList.add('d-none')
    if (this.hasConfirmationTarget) this.confirmationTarget.classList.add('d-none')
    if (this.hasBudgetNoticeTarget) this.budgetNoticeTarget.classList.add('d-none')

    this.signatureTarget.classList.add('d-none')
    this.errorTarget.classList.remove('d-none')
    this.#renderErrorDetails()
  }

  #renderErrorDetails() {
    let details = this.errorTarget.querySelector('[data-signature-debug]')

    if (!details) {
      details = document.createElement('p')
      details.setAttribute('data-signature-debug', '')
      details.classList.add('text-danger', 'fs-sm', 'mt-2')
      this.errorTarget.append(details)
    }

    details.textContent = this.lastError || ''
  }

  #setProgress(step) {
    this.progressTarget.textContent = `${step}/${Math.max(step, this.stepCountValue)}`
  }
}
